import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Eye, Users, MousePointerClick, FileCheck2, Globe, CalendarClock, Repeat } from "lucide-react";

const RANGES = [
  { k: 7, l: "7 jours" },
  { k: 30, l: "30 jours" },
  { k: 90, l: "90 jours" },
];

export default function Audience() {
  const [range, setRange] = useState(30);
  const [d, setD] = useState(null);

  const load = useCallback(() => {
    setD(null);
    return api.get(`/admin/analytics?range=${range}`).then((r) => setD(r.data));
  }, [range]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-head text-2xl font-bold text-[#1a2d56]">Audience & performance</h1>
          <p className="text-[#4b5b7c] text-sm mt-1">Fréquentation du site et tunnel de conversion (données premier-tiers).</p>
        </div>
        <div className="inline-flex rounded-full bg-[#f0f2f8] p-1" data-testid="range-toggle">
          {RANGES.map((r) => (
            <button
              key={r.k}
              data-testid={`range-${r.k}`}
              onClick={() => setRange(r.k)}
              className={`px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                range === r.k ? "bg-[#1a2d56] text-white" : "text-[#4b5b7c] hover:text-[#1a2d56]"
              }`}
            >
              {r.l}
            </button>
          ))}
        </div>
      </div>

      {!d ? (
        <div className="text-[#9aa6c0]" data-testid="audience-loading">Chargement…</div>
      ) : (
        <div data-testid="audience-page" className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Kpi icon={Eye} label="Visiteurs aujourd'hui" value={d.unique_today} testid="kpi-today" />
            <Kpi icon={Users} label="Visiteurs uniques (7 j)" value={d.unique_7d} testid="kpi-7d" />
            <Kpi icon={CalendarClock} label={`Visiteurs uniques (${d.range_days} j)`} value={d.unique_range} testid="kpi-range" accent />
            <Kpi icon={Repeat} label="Visiteurs réguliers" value={d.returning_visitors} sub={`${d.returning_rate}% reviennent`} testid="kpi-returning" />
            <Kpi icon={MousePointerClick} label={`Pages vues (${d.range_days} j)`} value={d.pageviews_total} testid="kpi-pageviews" />
          </div>

          <Card title="Tunnel de conversion" testid="funnel-card">
            <div className="grid sm:grid-cols-3 gap-4">
              <FunnelStep label="Visiteurs uniques" value={d.funnel.visitors} testid="funnel-visitors" />
              <FunnelStep label="Tests démarrés" value={d.funnel.tests_started} rate={`${d.funnel.start_rate}% des visiteurs`} testid="funnel-tests-started" />
              <FunnelStep label="Tests complétés (leads)" value={d.funnel.tests_completed} rate={`${d.funnel.complete_rate}% des tests`} accent testid="funnel-tests-completed" />
            </div>
          </Card>

          <Card title="Fréquentation quotidienne" testid="daily-card">
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={d.daily.map((x) => ({ name: x.day.slice(5), Visiteurs: x.visitors, Vues: x.views }))}>
                <defs>
                  <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1a2d56" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#1a2d56" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#4b5b7c" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#4b5b7c" }} />
                <Tooltip />
                <Area type="monotone" dataKey="Visiteurs" stroke="#1a2d56" strokeWidth={2.5} fill="url(#gv)" />
                <Area type="monotone" dataKey="Vues" stroke="#d7de3b" strokeWidth={2} fillOpacity={0} />
              </AreaChart>
            </ResponsiveContainer>
            {d.daily.length === 0 && <p className="text-sm text-[#9aa6c0] mt-2">Pas encore de données sur cette période.</p>}
          </Card>

          <div className="grid lg:grid-cols-2 gap-6">
            <Card title="Pages les plus vues" testid="top-pages">
              {d.top_pages.length === 0 ? (
                <p className="text-sm text-[#9aa6c0]">Aucune donnée.</p>
              ) : (
                <div className="space-y-2.5">
                  {d.top_pages.map((p) => (
                    <div key={p.path} className="flex items-center justify-between gap-3 text-sm" data-testid={`page-row`}>
                      <span className="text-[#1a2d56] font-medium truncate">{p.path}</span>
                      <span className="text-[#4b5b7c] shrink-0">{p.views} vues · {p.uniques} uniques</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card title="Sources de trafic" testid="sources-card">
              {d.sources.length === 0 ? (
                <p className="text-sm text-[#9aa6c0]">Aucune donnée.</p>
              ) : (
                <div className="space-y-3">
                  {d.sources.map((s) => {
                    const max = Math.max(1, ...d.sources.map((x) => x.visitors));
                    const pct = Math.round((s.visitors / max) * 100);
                    return (
                      <div key={s.source} data-testid="source-row">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-[#1a2d56] font-medium inline-flex items-center gap-1.5 capitalize">
                            <Globe size={13} className="text-[#9aa6c0]" /> {SOURCE_LABEL[s.source] || s.source}
                          </span>
                          <span className="text-[#4b5b7c]">{s.visitors}</span>
                        </div>
                        <div className="h-2.5 bg-[#f0f2f8] rounded-full overflow-hidden">
                          <div className="h-full bg-[#d7de3b] rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const SOURCE_LABEL = {
  direct: "Accès direct",
  search: "Moteurs de recherche",
  social: "Réseaux sociaux",
  referral: "Sites référents",
};

function Kpi({ icon: Icon, label, value, accent, testid, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-[#e2e8f0] p-5" data-testid={testid}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${accent ? "bg-[#d7de3b] text-[#1a2d56]" : "bg-[#f0f2f8] text-[#1a2d56]"}`}>
        <Icon size={18} />
      </div>
      <div className="font-head text-2xl font-extrabold text-[#1a2d56]">{value}</div>
      <div className="text-xs text-[#4b5b7c] mt-0.5">{label}</div>
      {sub && <div className="text-xs text-[#9aa6c0] mt-0.5">{sub}</div>}
    </div>
  );
}

function FunnelStep({ label, value, rate, accent, testid }) {
  return (
    <div className={`rounded-2xl p-5 ${accent ? "bg-[#1a2d56] text-white" : "bg-[#f8f9fc] border border-[#e2e8f0]"}`} data-testid={testid}>
      <div className={`font-head text-3xl font-extrabold ${accent ? "text-[#d7de3b]" : "text-[#1a2d56]"}`}>{value}</div>
      <div className={`text-sm font-semibold mt-1 ${accent ? "text-white" : "text-[#1a2d56]"}`}>{label}</div>
      {rate && <div className={`text-xs mt-1 ${accent ? "text-white/70" : "text-[#4b5b7c]"}`}>{rate}</div>}
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-[#e2e8f0] p-6" data-testid={testid}>
      <h2 className="font-head font-bold text-[#1a2d56] mb-4">{title}</h2>
      {children}
    </div>
  );
}
